import { useState } from 'react';
import { PortalLayout } from './components/PortalLayout';
import { Dashboard } from './components/Dashboard';
import { AskHR } from './components/AskHR';
import { Policies } from './components/Policies';
import { Card, CardContent, CardHeader, CardTitle } from './components/ui/card';
import { Button } from './components/ui/button';
import { Badge } from './components/ui/badge';
import { 
  Heart, 
  Calendar, 
  DollarSign, 
  Users, 
  Settings,
  Construction,
  Clock,
  CheckCircle,
  AlertCircle
} from 'lucide-react';

export default function App() {
  const [currentSection, setCurrentSection] = useState('dashboard');

  const renderSection = () => {
    switch (currentSection) {
      case 'dashboard':
        return <Dashboard />;
      case 'askhr':
        return <AskHR />;
      case 'policies':
        return <Policies />;
      case 'benefits':
        return <BenefitsSection />;
      case 'timeoff':
        return <TimeOffSection />;
      case 'payroll':
        return <PayrollSection />;
      case 'directory':
        return <DirectorySection />;
      case 'settings':
        return <SettingsSection />;
      default:
        return <Dashboard />;
    }
  };

  return (
    <PortalLayout 
      currentSection={currentSection} 
      onSectionChange={setCurrentSection}
    >
      {renderSection()}
    </PortalLayout>
  );
}

// Placeholder components for other sections
function BenefitsSection() {
  const benefits = [
    {
      category: 'Health Insurance',
      items: [
        { name: 'Medical PPO Plan', status: 'active', premium: '$245/month' },
        { name: 'Dental Insurance', status: 'active', premium: '$32/month' },
        { name: 'Vision Insurance', status: 'active', premium: '$18/month' },
      ]
    },
    {
      category: 'Retirement',
      items: [
        { name: '401(k) Plan', status: 'active', contribution: '8% + 6% match' },
        { name: 'Roth IRA Option', status: 'available', contribution: 'Not enrolled' },
      ]
    },
    {
      category: 'Wellness',
      items: [
        { name: 'Wellness Program', status: 'active', incentive: '$500/year' },
        { name: 'Mental Health Support', status: 'active', coverage: 'Unlimited' },
        { name: 'Gym Membership', status: 'active', discount: '25% off' },
      ]
    }
  ];

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold">Benefits & Wellness</h1>
          <p className="text-muted-foreground">
            View your current benefits and wellness programs
          </p>
        </div>
        <Button>
          <Heart className="h-4 w-4 mr-2" />
          Update Benefits
        </Button>
      </div>

      <div className="grid gap-6">
        {benefits.map((category) => (
          <Card key={category.category}>
            <CardHeader>
              <CardTitle>{category.category}</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="space-y-3">
                {category.items.map((item, index) => (
                  <div key={index} className="flex items-center justify-between p-3 border rounded-lg">
                    <div className="flex items-center gap-3">
                      <div className="flex items-center gap-2">
                        {item.status === 'active' ? (
                          <CheckCircle className="h-4 w-4 text-green-500" />
                        ) : (
                          <AlertCircle className="h-4 w-4 text-yellow-500" />
                        )}
                        <span className="font-medium">{item.name}</span>
                      </div>
                      <Badge variant={item.status === 'active' ? 'secondary' : 'outline'}>
                        {item.status}
                      </Badge>
                    </div>
                    <div className="text-sm text-muted-foreground">
                      {item.premium || item.contribution || item.incentive || item.coverage || item.discount}
                    </div>
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>
        ))}
      </div>
    </div>
  );
}

function TimeOffSection() {
  const timeOffData = [
    { type: 'PTO', balance: '18.5 days', used: '6.5 days', total: '25 days' },
    { type: 'Sick Leave', balance: 'Unlimited', used: '2 days', total: 'Unlimited' },
    { type: 'Personal Days', balance: '2 days', used: '1 day', total: '3 days' },
  ];

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold">Time Off & Scheduling</h1>
          <p className="text-muted-foreground">
            Manage your time off requests and view balances
          </p>
        </div>
        <Button>
          <Calendar className="h-4 w-4 mr-2" />
          Request Time Off
        </Button>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        {timeOffData.map((item) => (
          <Card key={item.type}>
            <CardHeader>
              <CardTitle className="text-lg">{item.type}</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="space-y-2">
                <div className="flex justify-between">
                  <span className="text-sm text-muted-foreground">Balance:</span>
                  <span className="font-medium">{item.balance}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-sm text-muted-foreground">Used:</span>
                  <span>{item.used}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-sm text-muted-foreground">Total:</span>
                  <span>{item.total}</span>
                </div>
              </div>
            </CardContent>
          </Card>
        ))}
      </div>

      <Card>
        <CardHeader>
          <CardTitle>Recent Requests</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="space-y-3">
            <div className="flex items-center justify-between p-3 border rounded-lg">
              <div>
                <div className="font-medium">Winter Vacation</div>
                <div className="text-sm text-muted-foreground">Dec 23 - Jan 2, 2024</div>
              </div>
              <Badge variant="outline">Pending</Badge>
            </div>
            <div className="flex items-center justify-between p-3 border rounded-lg">
              <div>
                <div className="font-medium">Sick Day</div>
                <div className="text-sm text-muted-foreground">Jan 8, 2024</div>
              </div>
              <Badge variant="secondary">Approved</Badge>
            </div>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}

function PayrollSection() {
  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold">Payroll & Compensation</h1>
          <p className="text-muted-foreground">
            Access paystubs, tax documents, and compensation details
          </p>
        </div>
        <Button>
          <DollarSign className="h-4 w-4 mr-2" />
          View Latest Paystub
        </Button>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
        <Card>
          <CardHeader>
            <CardTitle>Annual Salary</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">$65,000</div>
            <p className="text-sm text-muted-foreground">Base salary</p>
          </CardContent>
        </Card>
        <Card>
          <CardHeader>
            <CardTitle>Next Payday</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">Jan 19</div>
            <p className="text-sm text-muted-foreground">Bi-weekly</p>
          </CardContent>
        </Card>
        <Card>
          <CardHeader>
            <CardTitle>YTD Earnings</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">$2,500</div>
            <p className="text-sm text-muted-foreground">Gross pay</p>
          </CardContent>
        </Card>
        <Card>
          <CardHeader>
            <CardTitle>401(k) Match</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">$150</div>
            <p className="text-sm text-muted-foreground">This month</p>
          </CardContent>
        </Card>
      </div>

      <Card>
        <CardHeader>
          <CardTitle>Under Construction</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="text-center py-8">
            <Construction className="h-12 w-12 text-gray-400 mx-auto mb-3" />
            <p className="text-muted-foreground">
              Full payroll features are being developed. Contact HR for immediate needs.
            </p>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}

function DirectorySection() {
  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold">Employee Directory</h1>
          <p className="text-muted-foreground">
            Find and connect with your colleagues
          </p>
        </div>
      </div>

      <Card>
        <CardHeader>
          <CardTitle>Under Construction</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="text-center py-8">
            <Users className="h-12 w-12 text-gray-400 mx-auto mb-3" />
            <p className="text-muted-foreground">
              Employee directory is being developed. Contact HR for colleague information.
            </p>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}

function SettingsSection() {
  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold">Settings</h1>
          <p className="text-muted-foreground">
            Manage your account preferences and personal information
          </p>
        </div>
      </div>

      <Card>
        <CardHeader>
          <CardTitle>Under Construction</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="text-center py-8">
            <Settings className="h-12 w-12 text-gray-400 mx-auto mb-3" />
            <p className="text-muted-foreground">
              Settings page is being developed. Contact HR to update your information.
            </p>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}